import { useTranslation } from 'next-i18next';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { constraints } from 'src/constants/constraints.constants';
import { IExploreFilters, IPriceRange } from 'src/features/common/explore/ducks/explore-nfts/explore-nfts.types';
import { useScreenDetect } from 'src/hooks/useScreenDetect';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { Typography } from 'src/ui/common/atoms/typography';
import { Button } from 'src/ui/white-labels/techcontemporary/atoms/button';
import { StyledHelpMessage } from 'src/ui/white-labels/techcontemporary/atoms/text-input/text-input.styles';
import { useValidation } from 'src/utils/validation.utils';
import { PriceRangeWrapper, StyledInputWrapper, StyledSection, StyledTextInput } from './styles';

interface IProps {
    values: IPriceRange;
    onChange: (key: keyof IExploreFilters, payload: IPriceRange) => void;
}

const minFiatPrice = 1;

export const FilterPriceRange: React.VFC<IProps> = ({ values, onChange }) => {
    const screen = useScreenDetect();
    const { minValue, maxValue } = useValidation();
    const { t } = useTranslation('form');
    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        reValidateMode: 'onBlur',
        defaultValues: {
            min: values.from ? String(+values.from / 100) : '',
            max: values.to ? String(+values.to / 100) : '',
        },
    });

    const submit = useCallback(
        ({ min, max }) => {
            onChange('priceRange', {
                from: min,
                to: max,
            });
        },
        [onChange]
    );

    const min = watch('min');
    const max = watch('max');

    const generalError = t('validations.toLessFromError');

    const minLimitLocal = t('validations.smallMinValue', { limit: minFiatPrice });
    const minMaxLimitLocal = t('validations.smallMaxValue', { limit: max ? +max : constraints.collectible.price.max });

    const maxLimitLocal = t('validations.smallMinValue', { limit: min ? +min : minFiatPrice });
    const maxMaxLimitLocal = t('validations.smallMaxValue', { limit: constraints.collectible.price.max });

    const fromMinError = +min < minFiatPrice ? minLimitLocal : generalError;
    const toMinError = +max < minFiatPrice ? maxLimitLocal : generalError;

    const fromMaxError = +min > constraints.collectible.price.max ? minMaxLimitLocal : generalError;
    const toMaxError = +max > constraints.collectible.price.max ? maxMaxLimitLocal : generalError;

    useEffect(() => {
        setValue('min', values.from ? String(+values.from / 100) : '');
        setValue('max', values.to ? String(+values.to / 100) : '');
    }, [setValue, values.from, values.to]);

    return (
        <form onSubmit={handleSubmit(submit)} noValidate>
            <PriceRangeWrapper gap="10">
                <StyledInputWrapper flexDirection="column">
                    <FlexBox>
                        <StyledTextInput
                            {...register('min', {
                                ...minValue(minFiatPrice, fromMinError),
                                ...maxValue(max ? +max : constraints.collectible.price.max, fromMaxError),
                            })}
                            type="number"
                            autoFocus
                            placeholder="Min"
                        />
                        <StyledSection>
                            <Typography color="gs100" size="sm">
                                €
                            </Typography>
                        </StyledSection>
                    </FlexBox>
                    {errors?.min?.message && (
                        <StyledHelpMessage state="error">{errors?.min?.message}</StyledHelpMessage>
                    )}
                </StyledInputWrapper>

                <StyledInputWrapper flexDirection="column">
                    <FlexBox>
                        <StyledTextInput
                            {...register('max', {
                                ...minValue(min ? +min : minFiatPrice, toMinError),
                                ...maxValue(constraints.collectible.price.max, toMaxError),
                            })}
                            type="number"
                            placeholder="Max"
                        />
                        <StyledSection>
                            <Typography color="gs100" size="sm">
                                €
                            </Typography>
                        </StyledSection>
                    </FlexBox>
                    {errors?.min?.message ? undefined : (
                        <StyledHelpMessage state="error">{errors?.max?.message}</StyledHelpMessage>
                    )}
                </StyledInputWrapper>
            </PriceRangeWrapper>
            <Button
                type="submit"
                disabled={!min && !max}
                size={screen.xs || screen.sm ? 'md' : 'lg'}
                stretch
                variant="tonal"
            >
                {t('explore.actions.apply', { ns: 'tech-contemporary' })}
            </Button>
        </form>
    );
};